import React, { useState, useMemo } from 'react';
import {
  KeyOutlined,
  LockOutlined,
  LockOpenOutlined,
  ManageAccountsOutlined,
  LogoutOutlined,
  LoginOutlined,
  SecurityOutlined,
  VpnKeyOutlined,
  HistoryOutlined,
  ToggleOnOutlined,
  PersonAddOutlined,
  AdminPanelSettingsOutlined,
  SearchOutlined,
  LocalFireDepartmentOutlined,
  PsychologyOutlined,
  ShieldOutlined,
  ForestOutlined,
  WaterOutlined,
} from '@mui/icons-material';
import { translateEndpoint, translateMicroservice } from '../../../../utils/logTranslations';
import './UserActivityDetailChart.css';

/* ─── Constantes ─────────────────────────────────────────────────── */

const ENDPOINT_ICONS = {
  admin_update:          <ManageAccountsOutlined />,
  status_toggled:        <ToggleOnOutlined />,
  login:                 <LoginOutlined />,
  logout:                <LogoutOutlined />,
  register:              <PersonAddOutlined />,
  created_by_admin:      <AdminPanelSettingsOutlined />,
  created_user:          <PersonAddOutlined />,
  created_by_superadmin: <AdminPanelSettingsOutlined />,
  get_all:               <SearchOutlined />,
  password:              <LockOutlined />,
  reset:                 <LockOpenOutlined />,
  refresh:               <VpnKeyOutlined />,
  verify:                <SecurityOutlined />,
  token:                 <KeyOutlined />,
  default:               <HistoryOutlined />,
};

const MS_ICONS = {
  'historic-fire_service': <LocalFireDepartmentOutlined />,
  'physical_model':        <PsychologyOutlined />,
  'dfci_service':          <ShieldOutlined />,
  'vegetation_service':    <ForestOutlined />,
  'weather_service':       <WaterOutlined />,
};

const MS_COLORS_FALLBACK = {
  'historic-fire_service': '#be1622',
  'physical_model':        '#7C3AED',
  'dfci_service':          '#F97316',
  'vegetation_service':    '#16a34a',
  'weather_service':       '#64748B',
};

/* ─── Helpers ────────────────────────────────────────────────────── */

const getIcon = (name = '') => {
  const key = Object.keys(ENDPOINT_ICONS).find((k) => name.toLowerCase().includes(k));
  return ENDPOINT_ICONS[key] || ENDPOINT_ICONS.default;
};

const formatTime = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d)) return null;
  const now  = new Date();
  const diff = now - d;
  if (diff < 60_000)         return "À l'instant";
  if (diff < 3_600_000)      return `${Math.floor(diff / 60_000)} min`;
  if (diff < 86_400_000)     return `${Math.floor(diff / 3_600_000)} h`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)} j`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

/* ─── Empty state partagé ────────────────────────────────────────── */
const EmptyState = ({ icon, title, sub }) => (
  <div className="uad-empty-state">
    <div className="uad-empty-icon">{icon}</div>
    <span className="uad-empty-title">{title}</span>
    {sub && <span className="uad-empty-sub">{sub}</span>}
  </div>
);

/* ─── Composant ──────────────────────────────────────────────────── */
const UserActivityDetailChart = ({
  userId,
  username,
  activityData,
  loading,
  selectedMs,
  msConfigs,
  startDate,
  endDate,
}) => {

  const [expanded, setExpanded] = useState(null);
  const [filterMs, setFilterMs] = useState(null);

  const isLegacyMode = !selectedMs;

  const getMsColor = (k) => (msConfigs?.[k]?.color || MS_COLORS_FALLBACK[k] || '#64748b').trim();
  const getMsBg    = (k) => msConfigs?.[k]?.bg || `${getMsColor(k)}18`;
  // ← TRADUCTION : label du microservice en français
  const getMsLabel = (k) => msConfigs?.[k]?.label || translateMicroservice(k);

  /* ── TOUS LES HOOKS ─────────────────────────────────────────────── */

  const allFunctionality = useMemo(
    () => activityData?.by_functionality || [],
    [activityData],
  );

  const legacyEndpoints = useMemo(() => {
    if (!isLegacyMode) return [];
    const auth = allFunctionality.filter((ep) => {
      const n = (ep.endpoint || ep.name || '').toLowerCase();
      return n.startsWith('auth') || n.includes('auth');
    });
    return auth.length > 0 ? auth : allFunctionality;
  }, [isLegacyMode, allFunctionality]);

  const endpointsByMs = useMemo(() => {
    if (isLegacyMode) return {};
    const groups = {};
    allFunctionality.forEach((ep) => {
      const raw = ep.endpoint || ep.name || '';
      let k = ep.microservice || null;
      if (!k) {
        const full = ep.functionality || raw;
        if      (full.includes('historic') || full.includes('fire')) k = 'historic-fire_service';
        else if (full.includes('model'))   k = 'physical_model';
        else if (full.includes('dfci'))    k = 'dfci_service';
        else if (full.includes('vegeta'))  k = 'vegetation_service';
        else if (full.includes('weather')) k = 'weather_service';
        else if (raw.startsWith('auth'))   k = 'auth_service';
        else                               k = 'other';
      }
      if (!groups[k]) groups[k] = [];
      groups[k].push(ep);
    });
    return groups;
  }, [isLegacyMode, allFunctionality]);

  const visibleMsKeys = useMemo(() => {
    if (isLegacyMode) return [];
    const keys = Object.keys(endpointsByMs);
    if (!selectedMs || selectedMs.size === 0) return keys;
    return keys.filter((k) => selectedMs.has(k));
  }, [isLegacyMode, endpointsByMs, selectedMs]);

  const recentActivities = useMemo(() => {
    const all = activityData?.recent_activities || [];
    if (!startDate && !endDate) return all;
    const from = startDate ? (() => { const d = new Date(startDate); d.setHours(0,0,0,0); return d; })() : null;
    const to   = endDate   ? (() => { const d = new Date(endDate);   d.setHours(23,59,59,999); return d; })() : null;
    return all.filter((a) => {
      if (!a.timestamp) return false;
      const t = new Date(a.timestamp);
      if (from && t < from) return false;
      if (to   && t > to)   return false;
      return true;
    });
  }, [activityData, startDate, endDate]);

  const tsByEndpoint = useMemo(() => {
    const map = {};
    recentActivities.forEach((a) => {
      const key = a.functionality || a.service || a.endpoint || '';
      if (!map[key]) map[key] = [];
      map[key].push(a.timestamp);
    });
    return map;
  }, [recentActivities]);

  /* ── Early returns ──────────────────────────────────────────────── */

  if (!userId) return (
    <EmptyState
      icon={
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c8d2de" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="4"/>
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
        </svg>
      }
      title="Aucune sélection"
      sub={"Cliquez sur un utilisateur\npour voir son activité"}
    />
  );

  if (loading) return (
    <div className="uad-placeholder">
      <div className="uad-spinner" />
      <span>Chargement…</span>
    </div>
  );

  /* ══════════════════════════════════════════════════════════════════
     MODE LEGACY  (UserLogs — selectedMs absent)
  ══════════════════════════════════════════════════════════════════ */
  if (isLegacyMode) {
    const AUTH_COLOR = '#0EA5E9';
    const total = activityData?.total_activities
      || legacyEndpoints.reduce((s, ep) => s + (ep.total_usage || 0), 0);

    if (!legacyEndpoints.length && !recentActivities.length) return (
      <EmptyState
        icon={
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c8d2de" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4m0 0h18"/>
          </svg>
        }
        title="Aucune activité"
        sub={`Aucune donnée pour ${username}`}
      />
    );

    const sorted = [...legacyEndpoints].sort((a, b) => (b.total_usage || 0) - (a.total_usage || 0));

    return (
      <div className="uad-wrapper">
        <div className="uad-header">
          <div className="uad-meta-row">
            <span className="uad-chip uad-chip--calls">{total.toLocaleString()} appels</span>
            <span className="uad-chip uad-chip--funcs">{sorted.length} fonctionnalité{sorted.length > 1 ? 's' : ''}</span>
          </div>
        </div>
        <ul className="uad-ddp-list" style={{ '--panel-color': AUTH_COLOR }}>
          {sorted.map((ep, idx) => {
            // ← functionality (ex: "auth.login.dashboard") est la clé unique et précise
            // endpoint (ex: "login") est trop générique — dupliqué pour dashboard et SecufireAction
            const epFull = ep.functionality || '';
            const epRaw  = ep.endpoint || ep.name || epFull || '';
            // ← TRADUCTION : on utilise functionality pour avoir le bon contexte
            const epName = epFull
              ? translateEndpoint(epFull, 'auth')
              : translateEndpoint(epRaw, 'auth');
            const count      = ep.total_usage || 0;
            const pct        = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
            const timestamps = tsByEndpoint[epFull] || tsByEndpoint[epRaw] || tsByEndpoint[`auth.${epRaw}`] || [];
            const lastTs     = timestamps[0] || null;
            const isOpen     = expanded === idx;
            const hasTimestamps = timestamps.length > 0;
            return (
              <li
                key={epFull || epRaw || idx}
                className={`uad-ddp-item${hasTimestamps ? ' uad-ddp-item--clickable' : ''}`}
                onClick={() => hasTimestamps && setExpanded(isOpen ? null : idx)}
              >
                <div className="uad-ddp-row">
                  <span className="uad-ddp-label">endpoint</span>
                  <span className="uad-ddp-value uad-ddp-value--ep">{epName || '—'}</span>
                </div>
                <div className="uad-ddp-row">
                  <span className="uad-ddp-label">appels</span>
                  <span className="uad-ddp-value" style={{ color: AUTH_COLOR, fontWeight: 700 }}>{count.toLocaleString()}</span>
                  <span className="uad-ddp-value uad-ddp-pct">{pct}%</span>
                </div>
                {lastTs && !isOpen && (
                  <div className="uad-ddp-row">
                    <span className="uad-ddp-label">dernier</span>
                    <span className="uad-ddp-value">
                      {new Date(lastTs).toLocaleString('fr-FR', {
                        day: '2-digit', month: 'short',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>
                )}
                {isOpen && hasTimestamps && (
                  <ul className="uad-ddp-ts" onClick={(e) => e.stopPropagation()}>
                    {timestamps.map((ts, i) => (
                      <li key={i} className="uad-ddp-ts-item">
                        <span className="uad-ddp-ts-dot" />
                        <span className="uad-ddp-ts-val">
                          {new Date(ts).toLocaleString('fr-FR', {
                            day: '2-digit', month: 'short',
                            hour: '2-digit', minute: '2-digit', second: '2-digit',
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════════════
     MODE AVANCÉ  (SecufireActionLogs — selectedMs présent)
  ══════════════════════════════════════════════════════════════════ */

  const displayMsKeys = filterMs ? [filterMs] : visibleMsKeys;
  const total = activityData?.total_activities
    || allFunctionality.reduce((s, ep) => s + (ep.total_usage || 0), 0);

  const hasData = displayMsKeys.some((k) => endpointsByMs[k]?.length > 0)
    || recentActivities.length > 0;

  if (!hasData) return (
    <EmptyState
      icon={
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c8d2de" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4m0 0h18"/>
        </svg>
      }
      title="Aucune activité"
      sub={`Aucune donnée pour ${username}`}
    />
  );

  return (
    <div className="uad-wrapper">

      <div className="uad-header">
        <span className="uad-username">{username}</span>
        <span className="uad-meta">
          {total.toLocaleString()} appels · {visibleMsKeys.length} service{visibleMsKeys.length > 1 ? 's' : ''}
        </span>
      </div>

      {visibleMsKeys.length > 1 && (
        <div className="uad-ms-filters">
          <button
            className={`uad-ms-filter-btn${!filterMs ? ' uad-ms-filter-btn--active' : ''}`}
            style={!filterMs ? { '--f-color': '#be1622', '--f-bg': 'rgba(190,22,34,0.08)' } : {}}
            onClick={() => setFilterMs(null)}
          >
            Tous
          </button>
          {visibleMsKeys.map((k) => (
            <button
              key={k}
              className={`uad-ms-filter-btn${filterMs === k ? ' uad-ms-filter-btn--active' : ''}`}
              style={{ '--f-color': getMsColor(k), '--f-bg': getMsBg(k) }}
              onClick={() => setFilterMs(filterMs === k ? null : k)}
            >
              <span className="uad-ms-filter-icon">{MS_ICONS[k] || <HistoryOutlined />}</span>
              {getMsLabel(k)}
            </button>
          ))}
        </div>
      )}

      <div className="uad-sections">
        {displayMsKeys.map((msKey) => {
          const eps = endpointsByMs[msKey] || [];
          if (!eps.length) return null;
          const sorted  = [...eps].sort((a, b) => (b.total_usage || 0) - (a.total_usage || 0));
          const msTotal = sorted.reduce((s, ep) => s + (ep.total_usage || 0), 0);
          const color   = getMsColor(msKey);
          const bg      = getMsBg(msKey);
          const label   = getMsLabel(msKey); // ← déjà traduit via translateMicroservice

          return (
            <div key={msKey} className="uad-section">
              <div className="uad-section-header" style={{ '--s-color': color, '--s-bg': bg }}>
                <span className="uad-section-icon">{MS_ICONS[msKey] || <HistoryOutlined />}</span>
                <span className="uad-section-label">{label}</span>
                <span className="uad-section-count">{msTotal.toLocaleString()} appels</span>
              </div>
              <ul className="uad-list">
                {sorted.map((ep, idx) => {
                  // ← functionality est unique (ex: "dfci_service.electric_line_view")
                  // endpoint peut être dupliqué (ex: "login" pour dashboard ET SecufireAction)
                  const epFull      = ep.functionality || '';
                  const rawNameValue = ep.endpoint || ep.name || epFull || '';
                  // ← TRADUCTION : functionality d'abord pour éviter les ambiguïtés
                  const translatedName = epFull
                    ? translateEndpoint(epFull, msKey)
                    : translateEndpoint(rawNameValue, msKey);
                  const count      = ep.total_usage || 0;
                  const pct        = msTotal > 0 ? (count / msTotal) * 100 : 0;
                  const itemKey    = `${msKey}-${idx}`;
                  const isOpen     = expanded === itemKey;
                  const timestamps = tsByEndpoint[epFull] || tsByEndpoint[rawNameValue] || tsByEndpoint[`${msKey}.${rawNameValue}`] || [];
                  const lastTs     = timestamps[0] || ep.last_call || null;
                  return (
                    <li
                      key={epFull || `${msKey}-${rawNameValue}` || idx}
                      className={`uad-ep${isOpen ? ' uad-ep--open' : ''}`}
                      style={{ '--ep-color': color }}
                    >
                      <div className="uad-ep-row" onClick={() => setExpanded(isOpen ? null : itemKey)}>
                        <span className="uad-ep-icon">{getIcon(rawNameValue)}</span>
                        <div className="uad-ep-body">
                          <div className="uad-ep-top">
                            <span className="uad-ep-name">{translatedName}</span>
                            {lastTs && <span className="uad-ep-time">{formatTime(lastTs)}</span>}
                          </div>
                          {!isOpen && (
                            <div className="uad-ep-track">
                              <div
                                className="uad-ep-fill"
                                style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, ${color}99)` }}
                              />
                            </div>
                          )}
                        </div>
                        <span className="uad-ep-count">{count.toLocaleString()}</span>
                      </div>
                      {isOpen && timestamps.length > 0 && (
                        <ul className="uad-ts-list">
                          {timestamps.map((ts, i) => (
                            <li key={i} className="uad-ts-item">
                              <span className="uad-ts-dot" style={{ background: color }} />
                              <span className="uad-ts-val">
                                {new Date(ts).toLocaleString('fr-FR', {
                                  day: '2-digit', month: 'short',
                                  hour: '2-digit', minute: '2-digit', second: '2-digit',
                                })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UserActivityDetailChart;